/**
 * Template catalog lookup (S7, live-wired S7-R M1). `availableTemplates`
 * filters non-runnable templates FIRST (hidden, never greyed — per the
 * plan's quality bar), then searches by title/description/category.
 *
 * `serverVersion` and `capabilities` are now fed from a live probe
 * (`TemplatesPane` fetches `fetchServerVersionInfo` + `templateCapabilities`
 * per connection). The conservative defaults from the original S7 deviation
 * still hold as fallbacks whenever the probe hasn't resolved yet or fails
 * (mock connections with no seeded version response, a genuinely unreachable
 * server, etc.):
 *  - a `minimumVersion`-gated template is conservatively HIDDEN whenever
 *    `serverVersion` is unknown (never guessed as compatible); this is the
 *    safe default and doubles as the mock e2e's version-gate proof.
 *  - a `requiredCapabilities`-gated template is HIDDEN whenever the
 *    capability hasn't been proven present (same never-guess default).
 */
import type { DashboardEngineId } from '../types/dashboardTypes';
import type { DashboardTemplate, DashboardTemplateCapability } from './templateTypes';
import { mssqlTemplates } from './catalog/mssqlTemplates';
import { pgsqlTemplates } from './catalog/pgsqlTemplates';
import { mysqlTemplates } from './catalog/mysqlTemplates';
import { oracleTemplates } from './catalog/oracleTemplates';
import { sqliteTemplates } from './catalog/sqliteTemplates';
import { kustoTemplates } from './catalog/kustoTemplates';

const CATALOG_BY_ENGINE: Record<DashboardEngineId, DashboardTemplate[]> = {
  mssql: mssqlTemplates,
  pgsql: pgsqlTemplates,
  mysql: mysqlTemplates,
  oracle: oracleTemplates,
  sqlite: sqliteTemplates,
  kusto: kustoTemplates,
};

/**
 * Lightweight dotted-numeric version compare — good enough for the shapes
 * these catalogs use ('13', '16.2', '2019', '8.0.34'). Ignores non-numeric
 * suffixes ('19c' -> [19]) since none of our `minimumVersion` values need
 * that precision; a real semver/edition parser is out of scope here.
 */
export function compareVersions(a: string, b: string): number {
  const partsOf = (v: string) => (v.match(/\d+/g) ?? []).map(Number);
  const partsA = partsOf(a);
  const partsB = partsOf(b);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Extracts the dotted-numeric "product version" run most likely to BE the
 * server version out of a raw version string, per the shapes
 * `fetchServerVersionInfo` actually returns (S7-R M1):
 *  - pg:    `"PostgreSQL 16.4 on x86_64-..."` -> `"16.4"`
 *  - mysql: `"8.0.36"` / `"10.11.6-MariaDB"` -> `"8.0.36"` / `"10.11.6"`
 *  - mssql: `"Microsoft SQL Server 2022 (RTM-CU16) (KB5048033) - 16.0.4165.4
 *    (X64) ..."` -> `"16.0.4165.4"` (NOT the "2022" marketing year, which
 *    has no dot and would otherwise get grabbed first by a naive digit scan)
 * Prefers the first run with at least one dot (2-4 segments) since a bare
 * digit run earlier in the string is almost always a year/build-number
 * decoration, not the version proper. Falls back to the first digit run, or
 * the raw string, when no dotted run exists.
 */
export function extractProductVersion(versionString: string): string {
  const dotted = versionString.match(/\d+(?:\.\d+){1,3}/);
  if (dotted) return dotted[0];
  const bare = versionString.match(/\d+/);
  return bare ? bare[0] : versionString;
}

export function meetsMinimumVersion(
  serverVersion: string | undefined,
  minimumVersion: string | undefined,
): boolean {
  if (!minimumVersion) return true;
  if (!serverVersion) return false; // unconfirmed -> hide, never guess compatible
  return compareVersions(extractProductVersion(serverVersion), minimumVersion) >= 0;
}

function meetsCapabilities(
  capabilities: DashboardTemplateCapability[],
  required: DashboardTemplateCapability[] | undefined,
): boolean {
  if (!required || required.length === 0) return true;
  return required.every((cap) => capabilities.includes(cap));
}

function matchesSearch(template: DashboardTemplate, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    template.title.toLowerCase().includes(q) ||
    template.description.toLowerCase().includes(q) ||
    template.category.toLowerCase().includes(q)
  );
}

export function availableTemplates(
  engine: DashboardEngineId,
  serverVersion: string | undefined,
  capabilities: DashboardTemplateCapability[],
  query: string,
): DashboardTemplate[] {
  const catalog = CATALOG_BY_ENGINE[engine] ?? [];
  return catalog
    .filter((t) => meetsMinimumVersion(serverVersion, t.minimumVersion))
    .filter((t) => meetsCapabilities(capabilities, t.requiredCapabilities))
    .filter((t) => matchesSearch(t, query));
}

/**
 * Looks up a template by its catalog-wide unique id, regardless of engine —
 * S21 starter tiles persist `source: { kind: 'template', templateId }` and
 * resolve the template's `description` back for the first-run subtitle
 * (`components/tiles/TileTemplateSubtitle.tsx`) rather than duplicating the
 * text onto the tile itself (would go stale if the catalog copy changes).
 */
export function findTemplateById(id: string): DashboardTemplate | undefined {
  for (const templates of Object.values(CATALOG_BY_ENGINE)) {
    const found = templates.find((t) => t.id === id);
    if (found) return found;
  }
  return undefined;
}
